import { Avatar, Box, Grid, IconButton, Modal, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { DisplayName, GetDate, GetTime } from '../../../app/AppUtils';
import ExportExcelAttendance from '../../attendance/ExportExcelAttendance';
import { fetchAttendanceList, fetchExistingAuthorizations, resetAttendanceStatus, setFailedAuthorizations } from '../../attendance/attendanceSlice';
import { fetchAllContacts } from '../../contact/contactSlice';
import { fetchAvailableStudents } from '../../student/studentSlice';
import BackButton from '../../subcomponents/BackButton';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import ListItemCard from '../../subcomponents/ListItemCard';
import PageLayout from '../../subcomponents/PageLayout';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import { fetchPlanningPeriods } from '../planningSlice';
import SubscriptionsDisplay from '../subscriptions/SubscriptionsDisplay';
import UnsubscriptionsDisplay from '../subscriptions/UnsubscriptionsDisplay';
import { fetchSubscriptions, fetchUnsubscriptions, resetSubscriptionStatus } from '../subscriptions/subscriptionSlice';
import AuthorizationAddCard from './AuthorizationAddCard';
import AuthorizationsDisplay from './AuthorizationsDisplay';
import PlanningPerformanceEditCard from './PlanningPerformanceEditCard';
import { deletePerformance, fetchPerformance, fetchPlanningLocations, resetDefaultActivityValues, resetPlanningPerformanceStatus, selectPerformance, setFailedPerformance } from './planningPerformanceSlice';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';

export default function PlanningPerformancePage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { number, id } = useParams();

    const { aspNetUser } = useSelector((state) => state.settings.app);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { appSettings } = useSelector((state) => state.settings.app);
    const { performance, performanceStatus } = useSelector((state) => state.planning.performance);
    const { subscriptionsDelete, subscriptionsPut } = useSelector((state) => state.subscription);
    const { attendanceList } = useSelector((state) => state.attendance);
    const { activeDepartment } = useSelector((state) => state.settings.theme);

    const [open, setOpen] = useState(false);
    const [openAuthorization, setOpenAuthorization] = useState(false);

    const callbackModal = () => {
        setOpen(false);
        dispatch(resetPlanningPerformanceStatus());
        dispatch(resetDefaultActivityValues());
        dispatch(setFailedPerformance(false));
    };

    const authorizationCallbackModal = () => {
        setOpenAuthorization(false);
        dispatch(resetAttendanceStatus());
        dispatch(setFailedAuthorizations(false));
    };

    useEffect(() => {
        dispatch(fetchAvailableStudents(id));
        dispatch(fetchPerformance(id));
        dispatch(fetchSubscriptions(id));
        dispatch(fetchUnsubscriptions(id));
        dispatch(fetchExistingAuthorizations(id));
        dispatch(fetchAttendanceList(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (subscriptionsDelete.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetSubscriptionStatus());
            dispatch(fetchPerformance(id));
            dispatch(fetchSubscriptions(id));
            dispatch(fetchUnsubscriptions(id));
            dispatch(fetchAvailableStudents(id));
        } else if (subscriptionsDelete.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetSubscriptionStatus());
        };
    }, [subscriptionsDelete.status]);

    useEffect(() => {
        if (subscriptionsPut.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.UPDATE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetSubscriptionStatus());
            dispatch(fetchSubscriptions(id));
            dispatch(fetchUnsubscriptions(id));
            dispatch(fetchAvailableStudents(id));
        } else if (subscriptionsPut.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_UPDATE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetSubscriptionStatus());
        };
    }, [subscriptionsPut.status]);

    useEffect(() => {
        dispatch(fetchPlanningLocations());
        dispatch(fetchAllContacts());

        if (siomSettings.useMultipleDepartments && activeDepartment) {
            dispatch(fetchPlanningPeriods(activeDepartment[0]?.id));
        }

        if (!siomSettings.useMultipleDepartments) {
            dispatch(fetchPlanningPeriods(null));
        }
    }, [dispatch, siomSettings.useMultipleDepartments, activeDepartment]);

    const deleteClick = () => {
        dispatch(deletePerformance(id));
        dispatch(selectPerformance(null));
        navigate('/plannen');
    };

    const attendanceClick = () => {
        navigate(`/presentie/uitvoering/${performance.id}`);
    };

    const authorizationClick = () => {
        setOpenAuthorization(true);
    };

    const momentsClick = () => {
        navigate(`/plannen/momenten/${performance.id}`);
    };

    const yesNo = (val) => val ? resourceFinal[appLanguage].words.YES : resourceFinal[appLanguage].words.NO;
    const isAppV1 = appSettings?.appVersion === 1;
    const isAppV2 = appSettings?.appVersion === 2;
    const enablePerformanceName = siomSettings?.enablePerformanceName;
    const hasPlanningRole = aspNetUser?.role?.includes("Planning");
    const flagProgress = siomSettings?.flagProgress;
    const partialPayments = siomSettings?.enablePartialPayments;

    const gridItems = [
        // ROW 1
        {
            show: true,
            size: { xs: 12, md: isAppV2 || enablePerformanceName ? 5 : 6 },
            primaryText: performance?.activity?.name,
            secondaryText: resourceFinal[appLanguage].words.ACTIVITY,
        },
        {
            show: isAppV2 || enablePerformanceName,
            size: { xs: 12, md: 5 },
            primaryText: performance?.name || "-",
            secondaryText: resourceFinal[appLanguage].words.NAME,
        },
        {
            show: true,
            size: { xs: 12, md: isAppV2 || enablePerformanceName ? 2 : 6 },
            primaryText: performance?.number,
            secondaryText: resourceFinal[appLanguage].words.NUMBER,
        },

        // ROW 2
        {
            show: true,
            size: { xs: 12, md: 5 },
            primaryText: performance?.period?.name,
            secondaryText: resourceFinal[appLanguage].words.PERIOD,
        },
        {
            show: true,
            size: { xs: 12, md: 5 },
            primaryText: performance?.location?.name || "-",
            secondaryText: resourceFinal[appLanguage].words.LOCATION,
        },
        {
            show: true,
            size: { xs: 12, md: 2 },
            action: momentsClick,
            actionIcon: <ArrowForwardIosIcon color="secondary" />,
            primaryText: performance?.moments,
            secondaryText: performance?.moments === 1
                ? resourceFinal[appLanguage].words.MOMENT
                : resourceFinal[appLanguage].words.MOMENTS,
        },

        // ROW 3
        {
            show: true,
            size: { xs: 12, md: siomSettings?.enableUnsubscribeDays ? 3 : 4 },
            primaryText: `${GetDate(performance?.subscribableUntil)} (${GetTime(performance?.subscribableUntil)})`,
            secondaryText: resourceFinal[appLanguage].words.SUBSCRIBABLE_UNTIL,
        },
        {
            show: true,
            size: { xs: 12, md: siomSettings?.enableUnsubscribeDays ? 3 : 4 },
            primaryText: `${GetDate(performance?.momentStart)} (${GetTime(performance?.momentStart)} - ${GetTime(performance?.momentEnd)})`,
            secondaryText: `${resourceFinal[appLanguage].words.START}${resourceFinal[appLanguage].words.DATE.toLowerCase()}`,
        },
        {
            show: true,
            size: { xs: 12, md: siomSettings?.enableUnsubscribeDays ? 3 : 4 },
            primaryText: `${GetDate(performance?.subscribableFrom)} (${GetTime(performance?.subscribableFrom)})`,
            secondaryText: resourceFinal[appLanguage].words.SUBSCRIBABLE_FROM,
        },
        {
            show: siomSettings?.enableUnsubscribeDays,
            size: { xs: 12, md: 3 },
            primaryText: `${GetDate(performance?.unsubscribableUntil)} (${GetTime(performance?.unsubscribableUntil)})`,
            secondaryText: resourceFinal[appLanguage].words.UNSUBSCRIBED_DATE_MAX,
        },

        // ROW 4
        {
            show: true,
            size: { xs: 12, md: 4 },
            primaryText: performance?.contact
                ? DisplayName(performance?.contact?.firstname, performance?.contact?.insertion, performance?.contact?.lastname)
                : "-",
            secondaryText: resourceFinal[appLanguage].words.CONTACT,
        },
        {
            show: true,
            size: { xs: 12, md: 4 },
            primaryText: performance?.primaryAttendant
                ? DisplayName(performance?.primaryAttendant?.firstname, performance?.primaryAttendant?.insertion, performance?.primaryAttendant?.lastname)
                : "-",
            secondaryText: `${resourceFinal[appLanguage].words.PRIMARY} ${resourceFinal[appLanguage].words.ATTENDANT.toLowerCase()}`,
        },
        {
            show: true,
            size: { xs: 12, md: 4 },
            primaryText: performance?.secondaryAttendant
                ? DisplayName(performance?.secondaryAttendant?.firstname, performance?.secondaryAttendant?.insertion, performance?.secondaryAttendant?.lastname)
                : "-",
            secondaryText: `${resourceFinal[appLanguage].words.SECONDARY} ${resourceFinal[appLanguage].words.ATTENDANT.toLowerCase()}`,
        },

        // ROW 5
        {
            show: true,
            size: { xs: 12, md: !flagProgress ? 3 : 4 },
            primaryText: yesNo(performance?.studentRegistrationDisabled),
            secondaryText: resourceFinal[appLanguage].words.NONSUBSCRIBABLE,
        },
        {
            show: true,
            size: { xs: 12, md: !flagProgress ? 3 : 4 },
            primaryText: yesNo(performance?.alwaysAvailable),
            secondaryText: resourceFinal[appLanguage].words.ALWAYS_AVAILABLE,
        },
        {
            show: true,
            size: { xs: 12, md: !flagProgress ? 3 : 4 },
            primaryText: `${performance?.capacityCurrent}/${performance?.capacityLimit}`,
            secondaryText: resourceFinal[appLanguage].words.CAPACITY,
        },
        {
            show: !flagProgress,
            size: { xs: 12, md: 3 },
            primaryText: performance?.points || "-",
            secondaryText: isAppV1
                ? resourceFinal[appLanguage].words.POINTS
                : resourceFinal[appLanguage].words.HOURS,
        },

        // ROW 6 
        {
            show: isAppV1 && hasPlanningRole,
            size: { xs: 12, md: partialPayments ? 3 : 6 },
            primaryText: performance?.cost
                ? Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(performance?.cost)
                : "-",
            secondaryText: resourceFinal[appLanguage].words.COST,
        },
        {
            show: isAppV1 && hasPlanningRole,
            size: { xs: 12, md: partialPayments ? 3 : 6 },
            primaryText: performance?.extraCost
                ? Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(performance?.extraCost)
                : "-",
            secondaryText: resourceFinal[appLanguage].words.COST_EXTRA,
        },
        {
            show: isAppV1 && hasPlanningRole && partialPayments,
            size: { xs: 12, md: 3 },
            primaryText: yesNo(performance?.partialPaymentPosible),
            secondaryText: `${resourceFinal[appLanguage].words.PARTIAL_PAYMENT} ${resourceFinal[appLanguage].words.POSSIBLE.toLowerCase()}?`,
        },
        {
            show: isAppV1 && hasPlanningRole && partialPayments,
            size: { xs: 12, md: 3 },
            primaryText: performance?.partialPaymentAmount
                ? performance?.partialPaymentAmount
                : "-",
            secondaryText: `${resourceFinal[appLanguage].words.PARTIAL_PAYMENT} ${resourceFinal[appLanguage].words.COUNT.toLowerCase()}`,
        },

        // ROW 7
        {
            show: true,
            size: { xs: 12 },
            primaryText: performance?.remark || "-",
            secondaryText: resourceFinal[appLanguage].words.PERFORMANCE_REMARK,
        },
    ];


    return (
        <PageLayout
            title={`${performance?.activity?.name ? performance?.activity?.name + " - " : ""}${number}`}
            content={
                <ContentLoader
                    successCondition={performanceStatus === "success"}
                    errorCondition={performanceStatus === "failed"}
                    content={
                        <>
                            <Grid container spacing={1} sx={{ mt: { xs: 0, md: 1 } }}>
                                {gridItems
                                    .filter((item) => item.show)
                                    .map((item, i) => (
                                        <Grid key={i} size={item.size}>
                                            <ListItemCard
                                                backgroundStyle={{ height: 1 }}
                                                primaryText={item.primaryText}
                                                secondaryText={item.secondaryText}
                                                action={item.action}
                                                actionIcon={item.actionIcon}
                                            />
                                        </Grid>
                                    ))}

                                <Grid size={{ xs: 12 }}>
                                    <SubscriptionsDisplay performanceId={id} activityId={performance?.activity?.id} partialPaymentPosible={performance?.partialPaymentPosible} />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <UnsubscriptionsDisplay />
                                </Grid>
                                {(performance?.currentUserIsAssigned || aspNetUser?.role?.includes("Planning")) &&
                                    <Grid size={{ xs: 12 }}>
                                        <AuthorizationsDisplay performanceId={id} />
                                    </Grid>
                                }
                            </Grid>
                            <Modal open={open} onClose={callbackModal}>
                                <>
                                    <PlanningPerformanceEditCard handleClose={callbackModal} id={id} />
                                </>
                            </Modal>
                            <Modal open={openAuthorization} onClose={authorizationCallbackModal}>
                                <>
                                    <AuthorizationAddCard handleClose={authorizationCallbackModal} performanceId={id} />
                                </>
                            </Modal>
                        </>
                    }
                />
            } contentRight={
                <>
                    {performanceStatus === "success" && <>
                        {(performance?.currentUserIsAssigned || aspNetUser?.role?.includes("Attendances")) && <>
                            <Tooltip title={resourceFinal[appLanguage].words.AUTHORIZE} placement="left" arrow>
                                <IconButton onClick={authorizationClick}>
                                    <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                        <PersonAddAlt1Icon sx={{ color: 'white.main', fontSize: 40 }} />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            <ExportExcelAttendance disabled={attendanceList.status !== "success" || performance?.capacityCurrent <= 0} jsonData={attendanceList.data} />
                            <Tooltip title={resourceFinal[appLanguage].words.ATTENDANCE} placement="left" arrow>
                                <IconButton onClick={attendanceClick}>
                                    <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                        <HistoryEduIcon sx={{ color: 'white.main', fontSize: 40 }} />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                        </>}
                        {aspNetUser?.role?.includes("Planning") && <>
                            <Tooltip title={resourceFinal[appLanguage].words.EDIT} placement="left" arrow>
                                <IconButton onClick={() => setOpen(true)}>
                                    <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                        <EditIcon sx={{ color: 'white.main', fontSize: 40 }} />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            <Box>
                                <Tooltip title={resourceFinal[appLanguage].words.DELETE} placement="left" arrow>
                                    <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5, m: 1 }}>
                                        <ConfirmationModal iconButton={true} icon={<DeleteIcon sx={{ color: 'white.main', fontSize: 40 }} />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${performance?.name ? performance?.name : performance?.number} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={deleteClick} />
                                    </Avatar>
                                </Tooltip>
                            </Box>
                        </>}
                        <BackButton desktop enableBorder={performance?.currentUserIsAssigned || aspNetUser?.role?.includes("Planning")} />
                    </>}
                </>
            }
            bottomBar
            disableContentBackground
        />
    );
}
